import React, { ChangeEvent, KeyboardEvent, memo, RefObject } from 'react'

import styled from '@emotion/styled'

import { useHandler } from '@surglogs-app/hooks'
import { Cross } from '@surglogs-app/icons'

import { FlexBox } from '../../uiKit/components/FlexBox'
import { Icon } from '../../uiKit/components/Icon'
import { Input } from '../../uiKit/components/Input'
import { Text } from '../../uiKit/components/Text'

const StyledSearchBar = styled(FlexBox)`
  position: fixed;
  top: ${({ theme }) => theme.ui.spacings.md};
  right: ${({ theme }) => theme.ui.spacings.xl};
  z-index: 100;
  background: ${({ theme }) => theme.ui.colors['white.100']};
  border: 1px solid ${({ theme }) => theme.ui.colors['navy.10']};
  border-radius: ${({ theme }) => theme.ui.spacings.sm};
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
`

const StyledCloseButton = styled.button`
  cursor: pointer;
  border: 0;
  background: transparent;
  padding: 0;
  display: inline-flex;
  align-items: center;
`

interface VirtualizerSearchBarProps {
  inputRef: RefObject<HTMLInputElement | null>
  query: string
  activeMatchIndex: number
  totalMatches: number
  onQueryChange: (query: string) => void
  onClose: () => void
  goToNextMatch: () => void
  goToPreviousMatch: () => void
  searchPlaceholder: string
}

export const VirtualizerSearchBar = memo(
  ({
    inputRef,
    query,
    activeMatchIndex,
    totalMatches,
    onQueryChange,
    onClose,
    goToNextMatch,
    goToPreviousMatch,
    searchPlaceholder,
  }: VirtualizerSearchBarProps) => {
    const handleChange = useHandler((e: ChangeEvent<HTMLInputElement>) => {
      onQueryChange(e.target.value)
    })

    const handleKeyDown = useHandler((e: KeyboardEvent<HTMLInputElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault()
        if (e.shiftKey) {
          goToPreviousMatch()
        } else {
          goToNextMatch()
        }
      }
      if (e.key === 'Escape') {
        e.preventDefault()
        onClose()
      }
    })

    const matchLabel =
      totalMatches > 0
        ? `${activeMatchIndex + 1}/${totalMatches}`
        : query
        ? '0/0'
        : ''

    return (
      <StyledSearchBar {...{ ai: 'center', padding: 'sm', gap: 'sm' }}>
        <FlexBox {...{ flex: 1, ai: 'center', gap: 'sm' }}>
          <Input
            {...{
              ref: inputRef,
              value: query,
              onChange: handleChange,
              onKeyDown: handleKeyDown,
              placeholder: searchPlaceholder,
              'aria-label': 'virtualizer-search',
              inputSize: 'mini',
            }}
          />
          {matchLabel ? (
            <Text {...{ size: 'sm', color: 'navy.50', ws: 'nowrap' }}>
              {matchLabel}
            </Text>
          ) : null}
        </FlexBox>
        <StyledCloseButton
          {...{
            type: 'button',
            onClick: onClose,
            'aria-label': 'close-virtualizer-search',
          }}>
          <Icon {...{ icon: Cross, size: 'lg', color: 'navy.50' }} />
        </StyledCloseButton>
      </StyledSearchBar>
    )
  },
)
